import { useCallback, useEffect, useRef, useState } from "react";
import { FiLoader, FiX } from "react-icons/fi";
import { getAdminBusinessKyb, setAdminBusinessKybStatus } from "../api/admin";
import { ANNUAL_TURNOVER_RANGES } from "../constants/kyc";
import { useTranslation } from "../context/TranslationContext";

/**
 * What a business submitted for its KYB — an individual's answers or a company's —
 * everything an admin needs to approve or decline it, with the decision right there.
 *
 * Approving or declining notifies the owner; declining asks for a reason they'll see.
 *
 * @see mintly-back/src/controllers/adminKycController
 */

export const KYC_STATUS_OPTIONS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "declined", label: "Declined" },
];

const STATUS_TONE = {
  pending: "text-amber-500",
  approved: "text-emerald-500",
  declined: "text-rose-400",
};

/** Who a business is registered to, as a small label. */
export const APPLICANT_LABEL = { individual: "Individual", company: "Company" };

/**
 * Send a decision on a business, asking for a reason first when it's a decline.
 * Resolves to the API's answer, or null if the admin cancelled.
 */
export async function sendKybDecision(id, status, name) {
  let reason = "";
  if (status === "declined") {
    reason = window.prompt(
      `Decline ${name}?\n\nThe owner gets a notification. Reason (optional — they'll see it):`,
      ""
    );
    if (reason === null) return null;
  }
  return setAdminBusinessKybStatus(id, status, reason);
}

/** The status dropdown — or, when nothing was submitted, a note saying so. */
export function KycStatusSelect({ status, disabled, onChange, isDark, mutedText, label }) {
  if (!status || status === "none") {
    return <span className={`text-xs ${mutedText}`}>Not submitted</span>;
  }
  // Coloured by status, so its own text colour mustn't come from the shared input style.
  return (
    <select
      value={status}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      className={`rounded-xl border px-2 py-1.5 text-xs font-semibold outline-none disabled:opacity-50 ${
        isDark ? "border-white/15 bg-white/10" : "border-slate-300 bg-white"
      } ${STATUS_TONE[status] || ""}`}
    >
      {KYC_STATUS_OPTIONS.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Row({ label, children, s }) {
  return (
    <div className={`grid grid-cols-1 gap-1 border-b py-2 sm:grid-cols-[220px_1fr] sm:gap-4 ${s.rowBorder}`}>
      <dt className={`text-xs ${s.mutedText}`}>{label}</dt>
      <dd className={`break-words text-sm ${s.baseText}`}>{children || "—"}</dd>
    </div>
  );
}

function Section({ title, children, s }) {
  return (
    <section className="mt-6 first:mt-0">
      <h4 className={`mb-1 text-xs font-bold uppercase tracking-wide ${s.mutedText}`}>{title}</h4>
      <dl>{children}</dl>
    </section>
  );
}

const when = (iso) => {
  const d = new Date(iso);
  return iso && !Number.isNaN(d.getTime()) ? d.toLocaleString() : "";
};
const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : "—");

/**
 * @param {{ id: string, isDark: boolean, onClose: Function, onChanged?: Function, setToast?: Function }} props - id is the business's
 */
const AdminVerificationDetail = ({ id, isDark, onClose, onChanged, setToast }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const panel = "bg-bg-raised border-separator-default";
  const rowBorder = "border-separator-default";
  const s = { baseText, mutedText, rowBorder };

  // Callbacks the parent may recreate each render; reading them from a ref keeps a
  // parent's re-render from refetching.
  const callbacks = useRef({ setToast, onClose });
  callbacks.current = { setToast, onClose };

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getAdminBusinessKyb(id);
      setData(res.data);
    } catch (err) {
      callbacks.current.setToast?.(err?.response?.data?.message || "Could not load this verification");
      callbacks.current.onClose?.();
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && callbacks.current.onClose?.();
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const name = data?.business?.name;
  const app = data?.application;
  const individual = app?.applicantType === "individual";

  const decide = async (status) => {
    setBusy(true);
    try {
      const res = await sendKybDecision(id, status, name || "this business");
      if (!res) return;
      setToast?.(
        `${name || "Verification"} → ${status}${
          res.changed && status !== "pending" ? " · owner notified" : ""
        }`
      );
      await load();
      onChanged?.();
    } catch (err) {
      setToast?.(err?.response?.data?.message || "Could not change the status");
    } finally {
      setBusy(false);
    }
  };

  const activities = (a) =>
    (a.businessActivities || [])
      .map((x) => (x === "other" ? `Other: ${a.businessActivityOther}` : t(`kyc.activities.${x}`)))
      .join(", ");

  const personRows = (p, withShare) =>
    p && (
      <>
        <Row s={s} label="Full name">{p.fullName}</Row>
        <Row s={s} label="Personal ID number">{p.personalId}</Row>
        <Row s={s} label="Citizenship">{p.citizenship}</Row>
        {withShare && <Row s={s} label="Share in the company">{p.sharePercent != null ? `${p.sharePercent}%` : ""}</Row>}
        <Row s={s} label="Politically exposed person (PEP)">
          <span className={p.pep ? "font-semibold text-amber-500" : ""}>{yesNo(p.pep)}</span>
        </Row>
        <Row s={s} label="E-mail">{p.email}</Row>
        <Row s={s} label="Phone">{p.phone}</Row>
      </>
    );

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Business verification"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className={`my-8 w-full max-w-3xl overflow-hidden rounded-2xl border shadow-2xl ${panel}`}>
        <div className={`flex items-start justify-between gap-4 border-b px-6 py-4 ${rowBorder}`}>
          <div className="min-w-0">
            <p className={`text-[11px] font-bold uppercase tracking-wide ${mutedText}`}>
              Business verification (KYB)
              {app && ` · ${APPLICANT_LABEL[app.applicantType] || ""}`}
            </p>
            <h3 className={`text-lg font-bold ${baseText}`}>{name || (loading ? "…" : "—")}</h3>
            <p className={`text-xs ${mutedText}`}>
              {data?.business?.owner
                ? `Owner: ${data.business.owner.username} · ${data.business.owner.email}`
                : ""}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {data && (
              <KycStatusSelect
                status={data.status}
                disabled={busy}
                onChange={decide}
                isDark={isDark}
                mutedText={mutedText}
                label="Verification status"
              />
            )}
            <button
              type="button"
              onClick={onClose}
              className={`rounded-lg p-2 hover:bg-fill-quaternary`}
              aria-label="Close"
            >
              <FiX className={`h-5 w-5 ${mutedText}`} />
            </button>
          </div>
        </div>

        <div className="px-6 py-5">
          {loading ? (
            <div className="flex justify-center py-10">
              <FiLoader className="h-6 w-6 animate-spin text-mint" />
            </div>
          ) : !app ? (
            <p className={`py-10 text-center text-sm ${mutedText}`}>
              This business was created before verification existed and hasn't submitted the form yet.
            </p>
          ) : (
            <>
              <Section s={s} title="Decision">
                <Row s={s} label="Submitted">{when(app.submittedAt)}</Row>
                <Row s={s} label="Reviewed">
                  {app.reviewedAt ? `${when(app.reviewedAt)}${app.reviewedBy ? ` by ${app.reviewedBy}` : ""}` : "Not yet"}
                </Row>
                {app.status === "declined" && <Row s={s} label="Decline reason">{app.decisionReason}</Row>}
              </Section>

              {individual ? (
                <>
                  <Section s={s} title="Individual">
                    <Row s={s} label="Full name">{app.fullName}</Row>
                    <Row s={s} label="Personal ID number">{app.personalId}</Row>
                    <Row s={s} label="E-mail">{app.email}</Row>
                    <Row s={s} label="Phone">{app.phone}</Row>
                  </Section>
                  <Section s={s} title="Business activity">
                    <Row s={s} label="Type of business">{activities(app)}</Row>
                  </Section>
                </>
              ) : (
                <>
                  <Section s={s} title="General information">
                    <Row s={s} label="Company name / Full name">{app.companyName}</Row>
                    <Row s={s} label="Brand name">{app.brandName}</Row>
                    <Row s={s} label="Legal type">
                      {app.legalType === "other" ? `Other: ${app.legalTypeOther}` : t(`kyc.legalTypes.${app.legalType}`)}
                    </Row>
                    <Row s={s} label="Identification code / Personal ID">{app.identificationCode}</Row>
                    <Row s={s} label="Website">
                      {app.website && (
                        <a href={app.website} target="_blank" rel="noopener noreferrer" className="text-mint hover:underline">
                          {app.website}
                        </a>
                      )}
                    </Row>
                    <Row s={s} label="Bank account (IBAN)">{app.iban}</Row>
                    <Row s={s} label="Accepts cards from non-Georgian banks">{yesNo(app.acceptsForeignCards)}</Row>
                    <Row s={s} label="Estimated annual turnover">
                      {ANNUAL_TURNOVER_RANGES.find((r) => r.id === app.annualTurnover)?.label}
                    </Row>
                  </Section>
                  <Section s={s} title="Business activity">
                    <Row s={s} label="Type of business">{activities(app)}</Row>
                  </Section>
                  <Section s={s} title="Director (authorized representative)">{personRows(app.director)}</Section>
                  {(app.beneficialOwners || []).map((o, i) => (
                    <Section s={s} key={i} title={`Beneficial owner ${i + 1}`}>
                      {personRows(o, true)}
                    </Section>
                  ))}
                  <Section s={s} title="Declaration">
                    <Row s={s} label="Confirmed accurate and complete">{when(app.confirmedAt)}</Row>
                  </Section>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminVerificationDetail;
